import { Avatar, Button, Dropdown, MenuProps, Table } from 'antd';
import getDateWithTimeZone from 'helpers/getDateWithTimeZone';
import { TFunction } from 'i18next';
import { IColumn, INotes } from 'interfaces';
import React from 'react';
import classes from './NotesTab.module.scss';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store/hook';
import Assets from 'assets/icons';

const getLocaleColumns = (t: TFunction) => [
  {
    title: t('Documents.details.notes.author'),
    dataIndex: 'author',
    key: 'author',
    width: '22%',
  },
  {
    title: t('Documents.details.notes.text'),
    dataIndex: 'text',
    key: 'text',
    width: '56%',
  },
  {
    title: t('Documents.details.notes.timestamp_in_seconds'),
    dataIndex: 'timestamp_in_seconds',
    key: 'timestamp_in_seconds',
    width: 'calc(22% - 50px)',
    sorter: (a: INotes, b: INotes) => (a.timestamp_in_seconds || 0) - (b.timestamp_in_seconds || 1) ,
  },
  {
    title: '',
    dataIndex: 'details',
    key: 'details',
    width: '50px',
  },
];

type NotesTableProps = {
  onDeleteClick: (id: string) => void;
  onShareNote: (id: string) => void;
};

const NotesTable = ({onDeleteClick, onShareNote}: NotesTableProps) => {
  const [modifiedColumns, setModifiedColumns] = React.useState<IColumn[]>([]);
  
  const { notes, isNotesLoading } = useAppSelector((state) => state.notes);
  const { userData } = useAppSelector((state) => state.userData);

  const { t } = useTranslation();

  React.useEffect(()=> {
    const tableColumns = getLocaleColumns(t);
    const customColumns = tableColumns.map((col: IColumn) => ({
      ...col, 
      render: (value: string, info: INotes) => renderCell({value, info, col}),
    }))
    setModifiedColumns(customColumns);
  },[notes, isNotesLoading]);

  const renderCell = (props: any) => {
    const { value, info, col } = props;

    if (col.key === 'author') {
      return (
        <div className={classes.rowWrap}>
          <Avatar className={classes.avatar}>
            <span className={classes.avatarWord}>{info.author[0]}</span>
          </Avatar>
          <span className={classes.inviteColor}>{info.author}</span>
        </div>
      )
    };

    const menuItems: MenuProps['items'] = [];
    menuItems.push({
      label: (
        <span className={classes.spanItem}>
          <Assets.TrashIcon /> {t('Documents.contextMenu.delete')}
        </span>
      ),
      className: classes.menuItem,
      onClick: () => onDeleteClick(info.id),
      key: 'delete',
    });

    menuItems.push({
      type: 'divider',
      className: classes.divider,
    });

    menuItems.push({
      label: (
        <span className={classes.spanItem}>
          <Assets.ShareIcon /> {t('Documents.contextMenu.sharing')}
        </span>
      ),
      className: classes.menuItem,
      onClick: () => onShareNote(info.id),
      key: 'sharing',
    });
    
    if(col.key === 'timestamp_in_seconds'){
      return value && getDateWithTimeZone(value, userData?.date_format!);
    };

    if (col.key === 'details') {
      return info.can_share && (
        <Dropdown
          arrow={false}
          placement='bottomLeft'
          menu={{ items: menuItems }}
          classNames={{ root: classes.addMenu }}
        >
          <Button
            className={classes.detailIconBtn}
            size='small'
            type='text'
            icon={<Assets.VerticalIcon />}
          />
        </Dropdown>
      );
    };

    return value;
  };
  
  return (<Table
    columns={modifiedColumns}
    dataSource={notes ? notes.map(note => ({...note, key: note.id})).reverse() : []}
    pagination={false}
    size='small'
    scroll={{
      y: `calc(100dvh - 143px)`,
      // x: 'max-content'
    }}
  />
  );
}

export default NotesTable;
